import React, { useRef, useState, useEffect } from 'react';
import { Form, Chip } from '@openedx/paragon';
import { Close } from '@openedx/paragon/icons';

interface TagsChipsProps {
  tags: string[];
  tagInput: string;
  onTagInputChange: (value: string) => void;
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  label?: string;
  disabled?: boolean;
}

const TagsChips: React.FC<TagsChipsProps> = ({
  tags,
  tagInput,
  onTagInputChange,
  onAddTag,
  onRemoveTag,
  label = 'Tags',
  disabled = false,
}) => {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const overlayContainerRef = useRef<HTMLDivElement>(null);
  const [chipsWidth, setChipsWidth] = useState(0);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && tagInput.trim()) {
      e.preventDefault();
      const trimmedTag = tagInput.trim();
      if (!tags.includes(trimmedTag)) {
        if (overlayContainerRef.current && chipsContainerRef.current) {
          const containerWidth = overlayContainerRef.current.offsetWidth;
          const currentChipsWidth = chipsContainerRef.current.scrollWidth;
          const estimatedNewChipWidth = trimmedTag.length * 8 + 40;
          const minInputSpace = 120;
          if (currentChipsWidth + estimatedNewChipWidth + minInputSpace < containerWidth) {
            onAddTag(trimmedTag);
          }
        } else {
          onAddTag(trimmedTag);
        }
      }
    } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      e.preventDefault();
      const lastTag = tags[tags.length - 1];
      onRemoveTag(lastTag);
    }
  };

  useEffect(() => {
    if (chipsContainerRef.current) {
      const width = chipsContainerRef.current.offsetWidth;
      setChipsWidth(width);
    }
  }, [tags]);

  const displayValue = tags.length > 0 && !tagInput ? ' ' : tagInput;

  return (
    <Form.Group>
      <div style={{ position: 'relative' }}>
        <Form.Control
          ref={hiddenInputRef}
          type="text"
          floatingLabel={label}
          value={displayValue}
          onChange={(e) => onTagInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          style={{
            color: 'transparent',
            caretColor: 'black',
            paddingLeft: tags.length > 0 ? `${chipsWidth + 12}px` : '0.75rem',
          }}
        />
        <div
          ref={overlayContainerRef}
          style={{
            position: 'absolute',
            top: '50%',
            left: '0.75rem',
            right: '0.75rem',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            flexWrap: 'nowrap',
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
          onClick={() => hiddenInputRef.current?.focus()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              hiddenInputRef.current?.focus();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div
            ref={chipsContainerRef}
            style={{
              display: 'flex',
              gap: '0.375rem',
              alignItems: 'center',
              flexWrap: 'nowrap',
              flexShrink: 0,
            }}
          >
            {tags.filter((tag) => tag !== '').map((tag) => (
              <div key={tag} style={{ pointerEvents: 'auto' }}>
                <Chip
                  iconAfter={Close}
                  onIconAfterClick={() => onRemoveTag(tag)}
                  disabled={disabled}
                >
                  {tag}
                </Chip>
              </div>
            ))}
          </div>
          <div style={{
            flex: 1, minWidth: '120px', pointerEvents: 'none', color: 'black',
          }}
          >
            {tagInput}
          </div>
        </div>
      </div>
      <Form.Text>Press Tab to add a tag. Click the X icon or press backspace to remove a tag.</Form.Text>
    </Form.Group>
  );
};

export default TagsChips;
